/**
 * @file serverStore.ts
 * @description Server-side in-memory cache for SSR API request logs.
 *
 * Uses `globalThis` so the array survives Turbopack / Webpack Fast Refresh
 * module re-evaluation. Without this, every HMR update would wipe the cache
 * and the polling client would see an empty list.
 *
 * ⚠️  This file must NEVER be imported from client-side code.
 *     It is intentionally free of any 'use client' directive and relies on
 *     Next.js server/client bundle splitting to stay server-only.
 */

import type { ApiRequest } from './types';

// ─── GlobalThis Augmentation ──────────────────────────────────────────────────

declare global {
  // eslint-disable-next-line no-var
  var __apiDebuggerRequests: ApiRequest[] | undefined;
}

// ─── Lazy initialisation ──────────────────────────────────────────────────────

/**
 * Returns the global SSR request array, initialising it on first access.
 * Using a getter function (rather than a module-level `const`) ensures that
 * every call always reads the *current* globalThis reference even after HMR.
 */
function getStore(): ApiRequest[] {
  if (!globalThis.__apiDebuggerRequests) {
    globalThis.__apiDebuggerRequests = [];
  }
  return globalThis.__apiDebuggerRequests;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Returns a **copy** of all currently stored SSR requests (newest first). */
export function getServerRequests(): ApiRequest[] {
  return [...getStore()];
}

/**
 * Appends a new SSR request to the global cache.
 * Respects the optional `maxRequests` cap (defaults to 200 server-side
 * to match reasonable SSR traffic while keeping memory bounded).
 */
export function addServerRequest(
  request: ApiRequest,
  maxRequests = 200,
): void {
  const store = getStore();
  store.unshift(request);
  if (store.length > maxRequests) {
    store.length = maxRequests;
  }
}

/**
 * Patches an existing SSR request by ID (used in `onResponse` to update
 * `status`, `statusCode`, and `duration`).
 */
export function updateServerRequest(
  id: string,
  patch: Partial<Pick<ApiRequest, 'status' | 'statusCode' | 'duration'>>,
): void {
  const store = getStore();
  const idx = store.findIndex((r) => r.id === id);
  if (idx !== -1) {
    store[idx] = { ...store[idx]!, ...patch } as ApiRequest;
  }
}

/** Removes all SSR requests from the global cache. */
export function clearServerRequests(): void {
  globalThis.__apiDebuggerRequests = [];
}
